/*
 * Copyright (c) 2023, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useEffect} from 'react'
import {withRouter} from 'react-router-dom'
import loadable from '@loadable/component'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'

// Components
import {Box, Skeleton} from '@salesforce/retail-react-app/app/components/shared/ui'
import {configureRoutes} from '@salesforce/retail-react-app/app/utils/routes-utils'
import {routes as _routes} from '@salesforce/retail-react-app/app/routes'

const fallback = (
    <Box minHeight="100vh" width="100%">
        <Skeleton height="100vh" width="100%" />
    </Box>
)

// Create your pages here and add them to the routes array
// Use loadable to split code into smaller js chunks
const Home = loadable(() => import('./pages/home'), {fallback})
const ColorBarPLP = loadable(() => import('./pages/colorbar-plp'), {fallback})
const Cart = loadable(() => import('./pages/cart'), {fallback})
const ColorBarPDP = loadable(() => import('./pages/colorbar-pdp'), {fallback})
const ProductList = loadable(() => import('./pages/product-list'), {fallback})
const ProductDetail = loadable(() => import('./pages/product-detail'), {fallback})
const GiftCertificate = loadable(() => import('./pages/giftcert-purchase'), {fallback})
const ContentPage = loadable(() => import('./pages/content-page'), {fallback})
const PageNotFound = loadable(
    () => import('@salesforce/retail-react-app/app/pages/page-not-found'),
    {fallback}
)

// Checkout page from Adyen
const Checkout = loadable(() => import('./pages/checkout'), {
    fallback: fallback
})

// CheckoutConfirmation page from Adyen
const CheckoutConfirmation = loadable(() => import('./pages/checkout/confirmation'), {
    fallback: fallback
})

// Checkout Redirect page from Adyen
const AdyenCheckoutRedirect = loadable(() => import('./pages/checkout/redirect'), {
    fallback: fallback
})

// Checkout Error page from Adyen
const AdyenCheckoutError = loadable(() => import('./pages/checkout/error'), {
    fallback: fallback
})

const routes = [
    {
        path: '/',
        component: Home,
        exact: true
    },
    {
        path: '/yellow-rose.html',
        component: Home,
        exact: true
    },
    {
        path: '/custom-jewelry.html',
        component: ColorBarPLP,
        exact: true
    },
    {
        path: '*/colorbar.html',
        component: ColorBarPDP,
        exact: true
    },
    {
        path: '/',
        component: Home,
        exact: true
    },
    {
        path: '/cart',
        component: Cart,
        exact: true
    },
    {
        path: '/checkout/redirect',
        component: AdyenCheckoutRedirect
    },
    {
        path: '/checkout/error',
        component: AdyenCheckoutError
    },
    {
        path: '/checkout',
        component: Checkout,
        exact: true
    },
    {
        path: '/checkout/confirmation/:orderNo',
        component: CheckoutConfirmation
    },
    {
        path: '/yellow-rose/*/:productId.html',
        component: ProductDetail
    },
    {
        path: '/page/:pageId.html',
        component: ContentPage
    },
    {
        path: '/page/*/:pageId.html',
        component: ContentPage
    },
    {
        path: '/*/*/:productId.html',
        component: ProductDetail
    },
    {
        path: '/giftcertpurchase',
        component: GiftCertificate
    },
    {
        path: '/giftcertedit',
        component: GiftCertificate
    },
    {
        path: '/*/:productId.html',
        component: ProductDetail
    },
    {
        path: '/:pageId.html',
        component: ContentPage
    },
    {
        path: '/search',
        component: ProductList
    },
    {
        path: '/category/:categoryId',
        component: ProductList
    },
    {
        path: '/:categoryId',
        component: ProductList
    },
    {
        path: '/yellow-rose/search',
        component: ProductList
    },
    {
        path: '/yellow-rose/:categoryId',
        component: ProductList
    },
    ..._routes,
    {
        path: '*',
        component: withRouter((props) => {
            const {location} = props
            const urlParams = new URLSearchParams(location.search)

            useEffect(() => {
                const newURL = new URL(window.location)
                if (!urlParams.has('redirected')) {
                    newURL.searchParams.append('redirected', '1')
                    window.location.href = newURL
                }
            }, [window.location.href])
            if (urlParams.has('redirected')) {
                return <PageNotFound {...props} />
            }
            return null
        })
    }
]

export default () => {
    const config = getConfig()
    return configureRoutes(routes, config, {
        ignoredRoutes: ['/callback', '*']
    })
}
